import { useState, type ReactNode } from 'react'
import { Box, Stack } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { FileUpIcon, PlusIcon } from 'lucide-react'
import { Button, TABS_CONTENT_GAP } from '@repo/ui'

import { Header } from 'components'
import { SegmentedTabs } from 'components/navigation'
import { useCanAccess } from 'hooks'

import { EpisodesView } from './EpisodesView'
import { FormatsView } from './FormatsView'
import { GuestsView } from './GuestsView'
import { ImportSheetsModal } from './ImportSheetsModal'

/**
 * Episodes FIRST and the default (Spencer, 2026-10-29). The episode is the thing being made; a
 * guest is who it is made with and a segment is what it is made of, so arriving on Episodes puts
 * the work in front of you and the inputs one click away.
 */
export const VERAFIED_VIEWS = ['episodes', 'guests', 'formats'] as const
export type VerafiedView = (typeof VERAFIED_VIEWS)[number]
const DEFAULT_VIEW: VerafiedView = 'episodes'

const VIEW_LABELS: Record<VerafiedView, string> = { episodes: 'Episodes', guests: 'Guests', formats: 'Formats' }
const ADD_LABELS: Record<VerafiedView, string> = { episodes: 'Add episode', guests: 'Add guest', formats: 'Add segment' }

const DESCRIPTION =
  'The VERAfied interview series: who we could invite, the episodes from idea to published, and the segments an episode is built from. Invitations go out by hand; nothing here contacts anyone.'

/**
 * VRG-734 — GTM → Insights → Interviews. Three views, Episodes · Guests · Formats, kept in
 * `?view=` (every other param survives a switch, so a filtered view is a shareable link).
 *
 * Writes are platform_admin, matching the API. Everyone with GTM access reads, notes included:
 * notes are internal, so they appear on these internal pages and nowhere else (never in an
 * export, never in Ask Vera).
 */
export const VerafiedPanel = ({ title, tabs }: { title: string; tabs: ReactNode }) => {
  const [params, setParams] = useSearchParams()
  const requested = params.get('view')
  const view: VerafiedView = (VERAFIED_VIEWS as readonly string[]).includes(requested ?? '') ? (requested as VerafiedView) : DEFAULT_VIEW
  const { isHasAccess: canManage } = useCanAccess({ role: 'platform_admin' })
  const [adding, setAdding] = useState<VerafiedView | null>(null)
  const [importing, setImporting] = useState(false)

  const setView = (v: string) => {
    const next = new URLSearchParams(params)
    next.set('view', v)
    setParams(next, { replace: true })
  }

  return (
    <>
      <Header
        title={title}
        description={DESCRIPTION}
        rightComponents={
          canManage ? (
            <Stack direction="row">
              <Button variant="secondary" startIcon={<FileUpIcon size={18} strokeWidth={1} />} onClick={() => setImporting(true)}>
                Import sheets
              </Button>
              <Button variant="primary" startIcon={<PlusIcon size={18} strokeWidth={1} />} onClick={() => setAdding(view)}>
                {ADD_LABELS[view]}
              </Button>
            </Stack>
          ) : undefined
        }
      />
      {tabs}
      <SegmentedTabs tabs={VERAFIED_VIEWS.map((v) => ({ value: v, label: VIEW_LABELS[v] }))} value={view} onChange={setView} />
      <Box sx={{ mt: `${TABS_CONTENT_GAP}px` }}>
        {view === 'guests' ? (
          <GuestsView canManage={canManage} adding={adding === 'guests'} onAddingDone={() => setAdding(null)} />
        ) : view === 'episodes' ? (
          <EpisodesView canManage={canManage} adding={adding === 'episodes'} onAddingDone={() => setAdding(null)} />
        ) : (
          <FormatsView canManage={canManage} adding={adding === 'formats'} onAddingDone={() => setAdding(null)} />
        )}
      </Box>
      <ImportSheetsModal open={importing} onClose={() => setImporting(false)} />
    </>
  )
}
